// Only people signed in with access see the music (#410, user ruling Sep 2026:
// "only people who are signed in see the music — I might make some public or
// have a public option"). This reverses access-model.md's first decision,
// which had kept the music tools open to anonymous use.
//
// The gate is PRESENTATION, like every client-side switch: the content that
// needs locking (corpora, loops, transcriptions) is gated on the server as it
// moves there. What the gate does is decide what a visitor sees.
//
// A build with no accounts (unconfigured, e.g. the e2e build) stays open, as
// does a session whose client failed to load: there is nobody to sign in as.
// While the sign-in check runs, a device that remembers a profile with access
// shows the music at once (no flash on every reload); any other device waits
// quietly rather than flashing the sign-in page at a member.
//
// A route opens to everyone by joining PUBLIC_ROUTES. The ones there now
// handle sign-in themselves (an invite link, the playlist link page, the admin
// panel) or are
// pages anyone must be able to read (the privacy policy Google links to).
// A non-component module, so fast-refresh lint stays clean.

import { APP_ROUTES } from '@/navigation';
import { authConfigured, authStarted, hasCachedAccess, useAuth, type AuthStatus } from './session';

export type MusicAccess = 'open' | 'wait' | 'signin';

export interface MusicAccessInputs {
  status: AuthStatus;
  /** Is a Supabase project configured in this build? */
  configured: boolean;
  /** Has the client been asked to boot yet? */
  started: boolean;
  /** Does this device remember a profile that granted access? */
  cachedAccess: boolean;
}

export function musicAccessFor(inputs: MusicAccessInputs): MusicAccess {
  const { status, configured, started, cachedAccess } = inputs;
  if (status === 'active') return 'open';
  if (status === 'unconfigured') {
    // Before the boot has begun, 'unconfigured' is just the starting value.
    if (configured && !started) return cachedAccess ? 'open' : 'wait';
    return 'open';
  }
  if (status === 'loading') return cachedAccess ? 'open' : 'wait';
  return 'signin';
}

/** Routes anyone may open, signed in or not. */
export const PUBLIC_ROUTES: readonly string[] = [
  APP_ROUTES.privacy,
  APP_ROUTES.join,
  APP_ROUTES.sharedSetlist,
  APP_ROUTES.admin,
  APP_ROUTES.feedback,
];

export function isPublicRoute(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/';
  return PUBLIC_ROUTES.some((route) => path === route || path.startsWith(`${route}/`));
}

/** The same, for the signed-in viewer (the gate, the palette). */
export function useMusicAccess(): MusicAccess {
  const { status } = useAuth();
  return musicAccessFor({
    status,
    configured: authConfigured(),
    started: authStarted(),
    cachedAccess: hasCachedAccess(),
  });
}
